import React, { useEffect, useState } from 'react';
import { useOutreach } from '../../context/OutreachContext';
import SafeIcon from '../../common/SafeIcon';

const SectionEditor = ({ section, onChange, onDelete }) => {
  section = section || {};
  const handleChange = (field, value) => onChange({ ...section, [field]: value });
  const type = String(section.type || 'unknown').toLowerCase();

  return (
    <div className="bg-white p-4 rounded-xl border border-slate-200 mb-4 shadow-sm group">
      <div className="flex justify-between items-center mb-3">
        <span className="text-xs font-black uppercase tracking-widest text-slate-400">{section.type || 'Ukjent seksjonstype'}</span>
        <button onClick={onDelete} className="text-red-400 hover:text-red-600"><SafeIcon icon="🗑️" /></button>
      </div>
      
      <div className="space-y-3">
        {/* Common: Title */}
        <input 
            value={section.title || ''} 
            onChange={e => handleChange('title', e.target.value)} 
            placeholder="Overskrift" 
            className="w-full p-2 border rounded-lg text-sm font-bold" 
        />

        {/* Text Fields */}
        {(type === 'hero' || type === 'problem' || type === 'content' || type === 'cta') && (
          <textarea 
            value={section.text || section.subtitle || ''} 
            onChange={e => type === 'hero' ? handleChange('subtitle', e.target.value) : handleChange('text', e.target.value)} 
            placeholder={type === 'hero' ? "Undertittel..." : "Brødtekst..."}
            className="w-full p-2 border rounded-lg text-sm" 
            rows={3} 
          />
        )}

        {/* Hero Specifics */}
        {type === 'hero' && (
          <div className="grid grid-cols-2 gap-2">
            <input value={section.ctaText || ''} onChange={e => handleChange('ctaText', e.target.value)} placeholder="Knappetekst" className="p-2 border rounded-lg text-xs" />
            <select value={section.ctaAction || 'BookMeeting'} onChange={e => handleChange('ctaAction', e.target.value)} className="p-2 border rounded-lg text-xs">
              <option value="BookMeeting">Book Møte</option>
              <option value="LeadForm">Gå til skjema</option>
              <option value="OpenLink">Åpne URL</option>
            </select>
          </div>
        )}

        {/* CTA Specifics */}
        {type === 'cta' && (
          <div className="grid grid-cols-2 gap-2">
            <input value={section.ctaLabel || ''} onChange={e => handleChange('ctaLabel', e.target.value)} placeholder="Knappetekst" className="p-2 border rounded-lg text-xs" />
            <select value={section.ctaAction || 'reply_email'} onChange={e => handleChange('ctaAction', e.target.value)} className="p-2 border rounded-lg text-xs">
                <option value="reply_email">Svar på e-post</option>
                <option value="BookMeeting">Book Møte</option>
            </select>
          </div>
        )}

        {/* List Items */}
        {type === 'list' && (
            <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Punkter (kommaseparert)</label>
                <textarea 
                    value={Array.isArray(section.items) ? section.items.join('\n') : section.items || ''} 
                    onChange={e => handleChange('items', e.target.value.split('\n'))}
                    className="w-full p-2 border rounded-lg text-xs font-mono"
                    rows={4}
                    placeholder="Punkt 1&#10;Punkt 2&#10;Punkt 3"
                />
            </div>
        )}
      </div>
    </div>
  );
};

const MicrositeBuilder = () => {
  const { microsites, saveMicrosite, campaigns } = useOutreach();
  const [activeId, setActiveId] = useState(null);
  const [pendingSlug, setPendingSlug] = useState('');
  const [formData, setFormData] = useState({ title: '', slug: '', sections: [], campaign_id: '' });

  const activeSite = microsites.find(m => m.id === activeId);

  const handleCreate = () => {
    const newSite = { title: 'Ny Side', slug: `page-${Date.now()}`, content: { sections: [] }, status: 'Draft' };
    setPendingSlug(newSite.slug);
    saveMicrosite(newSite);
    // In real app, wait for ID. Here we mock finding it immediately or managing state better.
  };

  const handleSave = () => {
    if (!activeId || !activeSite) return;
    saveMicrosite({ ...activeSite, ...formData, content: { sections: formData.sections } });
    alert('Lagret!');
  };

  const addSection = (type) => {
    setFormData(prev => ({ ...prev, sections: [...prev.sections, { type, title: '', text: '' }] }));
  };

  const updateSection = (idx, updated) => {
    const newSections = [...formData.sections];
    newSections[idx] = updated;
    setFormData(prev => ({ ...prev, sections: newSections }));
  };

  const deleteSection = (idx) => {
    setFormData(prev => ({ ...prev, sections: prev.sections.filter((_, i) => i !== idx) }));
  };

  // Load into form when selecting a site
  const selectSite = (site) => {
    setActiveId(site.id);
    setFormData({
      title: site.title,
      slug: site.slug,
      sections: site.content?.sections || [],
      campaign_id: site.campaign_id || ''
    });
  };

  useEffect(() => {
    if (!pendingSlug) return;
    const createdSite = microsites.find(site => site.slug === pendingSlug);
    if (createdSite) {
      selectSite(createdSite);
      setPendingSlug('');
    }
  }, [microsites, pendingSlug]);

  return (
    <div className="flex h-[calc(100vh-100px)] gap-6">
      {/* Sidebar List */}
      <div className="w-64 bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-100 flex justify-between items-center">
          <h3 className="font-black text-slate-900 text-sm">Dine Sider</h3>
          <button onClick={handleCreate} className="text-primary-600"><SafeIcon icon="➕" /></button>
        </div>
        <div className="px-3 pt-3">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <p className="font-black">Microsite Builder / V2 – ikke aktivert</p>
            <p className="mt-1">Denne modulen er foreløpig ikke aktivert for operativ bruk. Den er ikke koblet til permanent lagring eller LinkedIn-prospects ennå.</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {microsites.map(site => (
            <button 
              key={site.id} 
              onClick={() => selectSite(site)}
              className={`w-full text-left p-3 rounded-xl text-sm font-bold ${activeId === site.id ? 'bg-primary-50 text-primary-700' : 'hover:bg-slate-50 text-slate-700'}`}
            >
              {site.title}
              <span className="block text-[10px] text-slate-400 font-normal">/{site.slug}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Editor Area */}
      {activeId && activeSite ? (
        <div className="flex-1 flex gap-6">
          {/* Config & Sections */}
          <div className="w-[400px] flex flex-col gap-4 overflow-y-auto">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
              <h3 className="font-black text-slate-900">Konfigurasjon</h3>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Tittel</label>
                <input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} className="w-full p-2 border rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">URL Slug</label>
                <input value={formData.slug} onChange={e => setFormData({ ...formData, slug: e.target.value })} className="w-full p-2 border rounded-lg text-sm bg-slate-50 font-mono" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Kampanje</label>
                <select value={formData.campaign_id} onChange={e => setFormData({ ...formData, campaign_id: e.target.value })} className="w-full p-2 border rounded-lg text-sm">
                  <option value="">Velg kampanje...</option>
                  {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <button onClick={handleSave} className="w-full py-2 bg-primary-600 text-white rounded-lg font-bold text-sm">Lagre Endringer</button>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-black text-slate-400 uppercase tracking-widest pl-2">Seksjoner</p>
              {formData.sections.map((sec, idx) => (
                <SectionEditor key={idx} section={sec} onChange={(upd) => updateSection(idx, upd)} onDelete={() => deleteSection(idx)} />
              ))}
              
              <div className="grid grid-cols-2 gap-2">
                {['Hero', 'Content', 'List', 'CTA', 'LeadForm'].map(type => (
                  <button key={type} onClick={() => addSection(type)} className="p-2 border border-dashed border-slate-300 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-50">
                    + {type}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Live Preview */}
          <div className="flex-1 bg-slate-100 rounded-[2rem] border-4 border-slate-200 overflow-hidden shadow-inner relative">
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-slate-800 text-white px-3 py-1 rounded-full text-[10px] font-bold z-10 opacity-50">Mobile Preview</div>
            <div className="h-full w-full bg-white overflow-y-auto">
              <div className="max-w-md mx-auto min-h-full bg-white shadow-2xl my-8 border border-slate-100">
                {/* Simulated Content */}
                {formData.sections.length === 0 ? (
                  <div className="min-h-[240px] flex items-center justify-center p-8 text-center text-sm text-slate-400">
                    Ingen seksjoner ennå. Legg til en seksjon for å se forhåndsvisningen.
                  </div>
                ) : formData.sections.map((sec, idx) => {
                    sec = sec || {};
                    const type = String(sec?.type || 'content').toLowerCase();
                    return (
                        <div key={idx} className="p-8 border-b border-slate-50">
                            {type === 'hero' && (
                            <div className="text-center space-y-4">
                                <h1 className="text-2xl font-black text-slate-900">{sec.title || 'Overskrift'}</h1>
                                <p className="text-slate-600">{sec.subtitle || sec.text}</p>
                                <button className="px-6 py-3 bg-blue-600 text-white rounded-full font-bold text-sm">{sec.ctaText || 'Klikk her'}</button>
                            </div>
                            )}
                            
                            {(type === 'content' || type === 'problem') && (
                            <div>
                                <h2 className="text-lg font-bold text-slate-800 mb-2">{sec.title}</h2>
                                <p className="text-sm text-slate-600 leading-relaxed">{sec.text}</p>
                            </div>
                            )}

                            {type === 'list' && (
                            <div>
                                <h2 className="text-lg font-bold text-slate-800 mb-3">{sec.title}</h2>
                                <ul className="space-y-2">
                                    {Array.isArray(sec.items) && sec.items.map((item, i) => (
                                        <li key={i} className="flex gap-2 text-sm text-slate-600">
                                            <span>✅</span> {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            )}

                            {type === 'cta' && (
                            <div className="text-center bg-blue-50 p-6 rounded-xl">
                                <h2 className="text-lg font-bold text-blue-900 mb-2">{sec.title}</h2>
                                <p className="text-sm text-blue-800 mb-4">{sec.text}</p>
                                <button className="px-6 py-2 bg-blue-600 text-white rounded-full font-bold text-xs">
                                    {sec.ctaLabel || 'Action'}
                                </button>
                            </div>
                            )}

                            {type === 'leadform' && (
                            <div className="bg-slate-50 p-4 rounded-xl">
                                <h3 className="font-bold text-center mb-3">{sec.title || 'Kontakt oss'}</h3>
                                <div className="space-y-2">
                                <input disabled placeholder="Navn" className="w-full p-2 text-xs border rounded" />
                                <input disabled placeholder="E-post" className="w-full p-2 text-xs border rounded" />
                                <button disabled className="w-full py-2 bg-slate-900 text-white text-xs font-bold rounded">Send</button>
                                </div>
                            </div>
                            )}
                        </div>
                    );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center text-slate-400">
          {microsites.length === 0 ? 'Ingen microsites ennå. Opprett en ny side for å starte.' : 'Velg eller opprett en side for å redigere.'}
        </div>
      )}
    </div>
  );
};

export default MicrositeBuilder;